'use client';

import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Save, Download, Copy, Trash2, Plus, Edit3, FileText, Search } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { notesContent } from '../../data/notes-content';

interface Note {
    id: string;
    title: string;
    content: string;
    category: string;
    createdAt: string;
    updatedAt: string;
}

export default function NotesManager() {
    const [notes, setNotes] = useState<Note[]>([]);
    const [currentNote, setCurrentNote] = useState<Note | null>(null);
    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');
    const [category, setCategory] = useState('personal');
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [isEditing, setIsEditing] = useState(false);

    const { toast } = useToast();

    // Load notes from localStorage on mount
    useEffect(() => {
        const savedNotes = localStorage.getItem('notes');
        if (savedNotes) {
            try {
                setNotes(JSON.parse(savedNotes));
            } catch (error) {
                console.error('Failed to load notes:', error);
            }
        }
    }, []);

    // Save notes to localStorage whenever they change
    useEffect(() => {
        localStorage.setItem('notes', JSON.stringify(notes));
    }, [notes]);

    // Create or update note
    const saveNote = () => {
        if (!title.trim() || !content.trim()) {
            toast({
                title: 'Missing Information',
                description: 'Please enter both title and content.',
                variant: 'destructive',
            });
            return;
        }

        const now = new Date().toISOString();

        if (isEditing && currentNote) {
            // Update existing note
            const updatedNotes = notes.map(note =>
                note.id === currentNote.id
                    ? { ...note, title, content, category, updatedAt: now }
                    : note
            );
            setNotes(updatedNotes);
            toast({
                title: 'Note Updated',
                description: 'Your note has been updated successfully.',
            });
        } else {
            // Create new note
            const newNote: Note = {
                id: Date.now().toString(),
                title,
                content,
                category,
                createdAt: now,
                updatedAt: now,
            };
            setNotes([...notes, newNote]);
            toast({
                title: 'Note Created',
                description: 'Your note has been created successfully.',
            });
        }

        // Reset form
        setTitle('');
        setContent('');
        setCategory('personal');
        setCurrentNote(null);
        setIsEditing(false);
    };

    // Delete note
    const deleteNote = (id: string) => {
        const updatedNotes = notes.filter(note => note.id !== id);
        setNotes(updatedNotes);

        if (currentNote?.id === id) {
            setCurrentNote(null);
            setTitle('');
            setContent('');
            setCategory('personal');
            setIsEditing(false);
        }

        toast({
            title: 'Note Deleted',
            description: 'Your note has been deleted.',
        });
    };

    // Load note for editing
    const loadNote = (note: Note) => {
        setCurrentNote(note);
        setTitle(note.title);
        setContent(note.content);
        setCategory(note.category);
        setIsEditing(true);
    };

    // Create new note
    const createNewNote = () => {
        setCurrentNote(null);
        setTitle('');
        setContent('');
        setCategory('personal');
        setIsEditing(false);
    };

    // Copy note content
    const copyNote = (note: Note) => {
        navigator.clipboard.writeText(note.content).then(() => {
            toast({
                title: 'Copied',
                description: 'Note content has been copied to clipboard.',
            });
        });
    };

    // Download note as text file
    const downloadNote = (note: Note) => {
        const blob = new Blob([`${note.title}\n\n${note.content}\n\nCategory: ${note.category}\nCreated: ${new Date(note.createdAt).toLocaleString()}`], {
            type: 'text/plain'
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${note.title.replace(/\s+/g, '_')}.txt`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        toast({
            title: 'Download Started',
            description: 'Note is being downloaded.',
        });
    };

    // Filter notes
    const filteredNotes = notes.filter(note => {
        const matchesSearch = note.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            note.content.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = selectedCategory === 'all' || note.category === selectedCategory;
        return matchesSearch && matchesCategory;
    });

    // Get unique categories
    const categories = ['all', ...Array.from(new Set(notes.map(note => note.category)))];

    return (
        <>
            <Toaster />
            <div className="min-h-screen bg-gray-50 py-12 px-6 sm:px-8">
                <div className="max-w-6xl mx-auto">
                    <ToolPageIntro title="Online Notes - Free Digital Notepad">
                Create, edit, and manage your notes online with our free digital notepad. Perfect for quick note-taking, brainstorming, and organizing thoughts.
            </ToolPageIntro>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {/* Notes List */}
                        <div className="lg:col-span-1">
                            <Card>
                                <CardHeader>
                                    <CardTitle className="flex items-center justify-between">
                                        <span>My Notes</span>
                                        <Button size="sm" onClick={createNewNote}>
                                            <Plus className="h-4 w-4 mr-2" />
                                            New
                                        </Button>
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-4">
                                        {/* Search */}
                                        <div className="relative">
                                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                            <Input
                                                placeholder="Search notes..."
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                className="pl-10"
                                            />
                                        </div>

                                        {/* Category Filter */}
                                        <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                                            <SelectTrigger>
                                                <SelectValue placeholder="Filter by category" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {categories.map(cat => (
                                                    <SelectItem key={cat} value={cat}>
                                                        {cat === 'all' ? 'All Categories' : cat}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>

                                        {/* Notes List */}
                                        <div className="space-y-2 max-h-96 overflow-y-auto">
                                            {filteredNotes.length === 0 ? (
                                                <p className="text-gray-500 text-center py-4">
                                                    No notes found
                                                </p>
                                            ) : (
                                                filteredNotes.map(note => (
                                                    <div
                                                        key={note.id}
                                                        className={`p-3 rounded-lg border cursor-pointer transition-colors ${currentNote?.id === note.id
                                                            ? 'border-blue-500 bg-blue-50'
                                                            : 'border-gray-200 hover:border-gray-300'
                                                            }`}
                                                        onClick={() => loadNote(note)}
                                                    >
                                                        <div className="flex items-start justify-between">
                                                            <div className="flex-1 min-w-0">
                                                                <h4 className="font-medium text-gray-900 truncate">
                                                                    {note.title}
                                                                </h4>
                                                                <p className="text-sm text-gray-500 truncate">
                                                                    {note.category}
                                                                </p>
                                                                <p className="text-xs text-gray-400">
                                                                    {new Date(note.updatedAt).toLocaleDateString()}
                                                                </p>
                                                            </div>
                                                            <div className="flex space-x-1 ml-2">
                                                                <Button
                                                                    size="sm"
                                                                    variant="ghost"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        copyNote(note);
                                                                    }}
                                                                >
                                                                    <Copy className="h-3 w-3" />
                                                                </Button>
                                                                <Button
                                                                    size="sm"
                                                                    variant="ghost"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        deleteNote(note.id);
                                                                    }}
                                                                >
                                                                    <Trash2 className="h-3 w-3" />
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>

                        {/* Note Editor */}
                        <div className="lg:col-span-2">
                            <Card>
                                <CardHeader>
                                    <CardTitle className="flex items-center">
                                        <FileText className="h-5 w-5 mr-2" />
                                        {isEditing ? 'Edit Note' : 'Create Note'}
                                    </CardTitle>
                                    <CardDescription>
                                        {isEditing ? 'Edit your existing note' : 'Create a new note'}
                                    </CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-4">
                                        <div>
                                            <Label htmlFor="title">Title</Label>
                                            <Input
                                                id="title"
                                                value={title}
                                                onChange={(e) => setTitle(e.target.value)}
                                                placeholder="Enter note title..."
                                                className="w-full"
                                            />
                                        </div>

                                        <div>
                                            <Label htmlFor="category">Category</Label>
                                            <Select value={category} onValueChange={setCategory}>
                                                <SelectTrigger>
                                                    <SelectValue placeholder="Select category" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="personal">Personal</SelectItem>
                                                    <SelectItem value="work">Work</SelectItem>
                                                    <SelectItem value="ideas">Ideas</SelectItem>
                                                    <SelectItem value="todo">To-Do</SelectItem>
                                                    <SelectItem value="important">Important</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>

                                        <div>
                                            <Label htmlFor="content">Content</Label>
                                            <Textarea
                                                id="content"
                                                value={content}
                                                onChange={(e) => setContent(e.target.value)}
                                                placeholder="Write your note here..."
                                                className="min-h-[300px] w-full"
                                            />
                                        </div>

                                        <div className="flex space-x-2">
                                            <Button onClick={saveNote} className="flex-1">
                                                <Save className="h-4 w-4 mr-2" />
                                                {isEditing ? 'Update' : 'Save'}
                                            </Button>

                                            {currentNote && (
                                                <Button
                                                    variant="outline"
                                                    onClick={() => downloadNote(currentNote)}
                                                >
                                                    <Download className="h-4 w-4 mr-2" />
                                                    Download
                                                </Button>
                                            )}

                                            <Button
                                                variant="outline"
                                                onClick={createNewNote}
                                            >
                                                <Plus className="h-4 w-4 mr-2" />
                                                New
                                            </Button>
                                        </div>

                                        {/* Note Stats */}
                                        {notes.length > 0 && (
                                            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                                                <div className="grid grid-cols-2 gap-4 text-sm">
                                                    <div>
                                                        <span className="font-medium">Total Notes:</span> {notes.length}
                                                    </div>
                                                    <div>
                                                        <span className="font-medium">Categories:</span> {categories.length - 1}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    </div>

                    {/* Informational Content */}
                    <div className="mt-12">
                        <InformationalContent
                            title={notesContent.title}
                            description={notesContent.description}
                            content={notesContent.content}
                            faqs={notesContent.faqs}
                            relatedTools={notesContent.relatedTools}
                        />
                    </div>
                </div>
            </div>
        </>
    );
}
